/*  
    AI Suggest Module - 
    This is where the meat of the ract UI lives for AI Suggest. 
    The AI Suggest right pane inners and all its subsections are rendered from this file. 
    TODO: this is where you will hook into the data that is returned from the new ai suggest store service api.
*/

import React from "react";
import {Simulate} from "react-dom/test-utils";
import PropTypes from "prop-types";
import maskFormats from "lib/common/descriptorcontrols/BaseControls/InputMask/MaskConstants";

const highlightText = (text = "", words = []) => {
    const out = text;
    words.forEach((word) => {
        const reg = new RegExp(`${word}`, "gi");
        out.replace(reg, `<span class="answer-highlight">${word}</span>`);
    });
    return out;
};
const AbstractionAiSuggestion = ({answer, keyboardShortcuts, referenceDocuments, onClick, feedbackEnabled, onFeedback, getFeedback}) => {
    const [expand, setExpand] = React.useState(null);
    let total = 0;
    let hasShortcuts = false;
    const suggestion = answer.suggestion;
    const primarySuggestion = suggestion.suggestion_value !== null ? [suggestion.suggestion_value.toString()] : [];
    const alternatives = !!primarySuggestion.length ? suggestion.alternatives.map((alt) => alt.toString()) : [];
    const fieldSuggestions = primarySuggestion.concat(alternatives);
    const answersInfo = fieldSuggestions.map((label) => {
        let keyboardShortcut = keyboardShortcuts.find((sc) => sc.name === label);
        if(!!keyboardShortcut) {
            keyboardShortcut = keyboardShortcut.key;
            hasShortcuts = true;
        }
        return {
            label: label,
            keyboardShortcut: keyboardShortcut
        };
    });
    const handleKeydown = (e) => {
        if(!hasShortcuts) {
            return; // no shortcuts "`" does nothing.
        }
        if(e.key === "`") {
            const shortname = "suggest";
            const qs = `[data-shortname="${shortname}"] .form-control`;
            const els = document.querySelectorAll(qs);
            if(els.length) {
                answersInfo.forEach((answer) => {
                    Simulate.keyDown(els[0], {"key": answer.keyboardShortcut}); // old react events
                    //els[0].dispatchEvent(new KeyboardEvent('keydown',{'key': keyboardShortcut})); // new react events
                });
            }
        }
    };

    React.useEffect(() => {
        window.addEventListener("keydown", handleKeydown);
        return () => {
            window.removeEventListener("keydown", handleKeydown);
        };
    }, [handleKeydown]);

    return (
        <React.Fragment>
            <div style={{color: "rgba( 0, 0, 0, .5)"}}>
                * Ai-Suggested Answer(s) are intended to supplement your review of all relevant records related to the episode.
            </div>
            <div className="section-title">Suggested Answer *</div>
            <ul className="keyboard-shortcuts">
                {answersInfo.map((answer) => (
                    <li key={answer.label}>
                        {!!answer.keyboardShortcut && (
                            <React.Fragment>
                                <kbd title={`Keyboard Shortcut = ${answer.keyboardShortcut} - ${answer.label}`}>{answer.keyboardShortcut}</kbd>= &nbsp;
                            </React.Fragment>
                        )}
                        <div className={"chip chip-blue"}>
                            <span className={"chip-content"}>{answer.label}</span>
                        </div>
                    </li>
                ))}
            </ul>
            <br/>
            {suggestion.reasoning && (<React.Fragment>
                <div className="section-title">Explanation for AI Suggested Answer</div>
                <p>{suggestion.reasoning}</p>

            </React.Fragment>)}

            <div className="section-title">Relevant Notes</div>
            <ol className={"evidence-list"}>
                {suggestion.segments.map(segment => (
                    segment.evidence.map(evidence => {
                        const key = `${segment.document_id}.${evidence.evidence_id}`;
                        const expanded = expand === key;
                        const evidenceText = highlightText(evidence.evidence_text, fieldSuggestions);
                        const segmentText = highlightText(segment.segment_text, fieldSuggestions);
                        total++;
                        const index = total;
                        const document = referenceDocuments[segment.document_id];
                        const feedback = getFeedback(evidence.evidence_id);
                        return (
                            <li key={key}>
                                <div dangerouslySetInnerHTML={{"__html": evidenceText}} />
                                <div className={"document-header"}>
                                    <span className={`fa ${expanded ? "fa-caret-down" : "fa-caret-right" }`} onClick={() => setExpand( expanded ? null : key)} />&nbsp;
                                    <span className="document-link" onClick={() => setExpand( expanded ? null : key)}>{document.document_type} - {moment(document.document_created_date).format(maskFormats.armus_date_day.momentjs)}</span>&nbsp;
                                    <span className={"document-details-icon fa fa-file-text-o"} onClick={(e) => onClick(e, {segment, evidence, index, answer: fieldSuggestions})} />

                                    {feedbackEnabled && (
                                    <span className={"evidence-feedback-group"}>
                                        <span
                                            className={`evidence-feedback feedback-good fa fa-thumbs-up ${(feedback === true ? "active" : "")}`}
                                            onClick={() => onFeedback(evidence.evidence_id, true)}
                                            title={"This relevant note is good!"}
                                        />
                                        <span
                                            className={`evidence-feedback feedback-bad fa fa-thumbs-down ${(feedback === false ? "active" : "")}`}
                                            onClick={() => onFeedback(evidence.evidence_id, false)}
                                            title={"This relevant note is bad."}
                                        />
                                    </span>)}
                                </div>
                                {expanded && (
                                    <div
                                        className={"document-text"}
                                        dangerouslySetInnerHTML={{"__html" : segmentText}}
                                    />
                                )}
                            </li>
                        );
                    })
                ))}
            </ol>
        </React.Fragment>
    );
};

AbstractionAiSuggestion.propTypes = {
    onClick: PropTypes.func.isRequired,
    feedbackEnabled: PropTypes.bool.isRequired,
    onFeedback: PropTypes.func.isRequired,
    getFeedback: PropTypes.func.isRequired,
    answer: PropTypes.object.isRequired,
    keyboardShortcuts: PropTypes.array,
    referenceDocuments: PropTypes.object.isRequired
};

const AbstractionAiDetails = ({details, referenceDocuments}) => {
    const {evidence, segment, answer, index} = details;
    const document = referenceDocuments[segment.document_id];
    const {document_created_date, document_type} = document;
    const documentText = highlightText(document.document_text, [segment.segment_text]);
    const evidence_text = highlightText(evidence.evidence_text, answer);

    return (
        <React.Fragment>
            <div className="section-title">Relevant Note</div>
            <ol>
                <li value={index} dangerouslySetInnerHTML={{"__html" : evidence_text}} />
            </ol>
            <div className="section-title">{document_type} - {moment(document_created_date).format(maskFormats.armus_date_day.momentjs)}</div>
            <div className="document-details" dangerouslySetInnerHTML={{"__html" : documentText}} />
        </React.Fragment>
    );
};

AbstractionAiDetails.propTypes = {
    details: PropTypes.object.isRequired,
    referenceDocuments: PropTypes.object.isRequired
};


const AbstractionAiModule = ({isLoading, open, onToggleOpen, name, error, answer, referenceDocuments, status, keyComponents, onNavToField, getFieldLabel, getEntityLabel, keyboardShortcuts, feedbackEnabled, onFeedback, getFeedback}) => {
    const [details, setDetails] = React.useState(null);
    const document = details ? referenceDocuments[details.segment.document_id] : {};
    let hasAnswer;
    let answerCount = 0;
    let statusMessage;
    switch(status) {
        case "NO_RECORD_FOUND":
            statusMessage = "No record found.";
            hasAnswer = false;
            break;
        case "APPLICATION_NOT_SUPPORTED":
            statusMessage = "Application not supported.";
            hasAnswer = false;
            break;
        case "IMPLEMENTATION_NOT_SUPPORTED":
            statusMessage = "Implementation not supported.";
            hasAnswer = false;
            break;
        case "ENTITY_NOT_SUPPORTED":
            statusMessage = "Entity not supported.";
            hasAnswer = false;
            break;
        case "INCOMPLETE_SUGGEST_KEY":
            statusMessage = (
                <React.Fragment>
                    Suggestions will be enabled when enough information has been entered to identify the record in the suggestion service. <br/><br/>
                    {!!keyComponents.length && <React.Fragment>Fields required to enable Abstraction AI are:<br/></React.Fragment>}
                    <ul>
                        {keyComponents.map(fieldInfo => {
                            const entityKey = fieldInfo.implementation+ "/" + fieldInfo.version + "/" + fieldInfo.entityName;
                            const recordId = fieldInfo.id;
                            const fieldKey = fieldInfo.fieldName;
                            const fieldLongName = getFieldLabel(entityKey, fieldKey);
                            const entityLongName = getEntityLabel(entityKey);
                            return (
                                <li key={entityKey+"/"+recordId+"/"+fieldKey} >
                                    {entityLongName}{" - "}
                                    {!!recordId ?
                                        <span
                                            className={"shortcut-link"}
                                            title={`Click here to navigate to ${entityLongName} and provide a value for ${fieldLongName}`}
                                            onClick={() => onNavToField(entityKey, recordId, fieldKey)}
                                        >{fieldLongName}</span>
                                        : <strong
                                            title={`Please navigate to a ${entityLongName} and provide a value for ${fieldLongName}`}
                                        >{fieldLongName}</strong>
                                    }
                                </li>
                            );
                        })}
                    </ul>
                </React.Fragment>
            );
            hasAnswer = false;
            break;
        case "NO_SUGGESTION_FILE":
            statusMessage = "No suggestion file available.";
            hasAnswer = false;
            break;
        case "NO_SUGGESTION_FIELD":
            statusMessage = "No suggestion field available.";
            hasAnswer = false;
            break;
        case "SUCCESS":
            statusMessage = "This field does not have any suggestions to offer at this time.";
            hasAnswer = !!answer.suggestion.suggestion_value.toString().length;
            answerCount = hasAnswer ? 1 + answer.suggestion.alternatives.length : 0;
            break;
        default:
            hasAnswer = false;
            statusMessage = "This field does not have any suggestions to offer at this time.";
    }
    const headingTitle = name ? `${name} - Suggestions (${answerCount})` : "";
    if(isLoading) {
        return (
            <div className="data-definitions suggestion-ai">
                <div className="panel-heading">
                    <span>{headingTitle}&#8203;</span>
                </div>
                <div className="panel-body">
                    <div className={"loading"}>
                        <img src="images/waiting.svg" width={25} height={25} alt="Loading..." />&nbsp;&nbsp;Loading suggestion...
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className={`data-definitions suggestion-ai ${open === null ? "neutral" : open ? "expanded" : "collapsed"}`}>
            <div className="panel-heading" onClick={onToggleOpen}>
                {error || !details || !hasAnswer ? (
                    <span>{headingTitle}&#8203;</span>
                ) : (
                    <div className="details-heading">
                        <div className={"document-details-close"} onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDetails(null);
                        }}><span className={"fa fa-chevron-left"} /></div>
                        <div className={"document-details-title"}>{document.document_type} - {moment(document.document_created_date).format(maskFormats.armus_date_day.momentjs)}</div>
                    </div>
                )}
            </div>
            <div className="panel-body">
                {error || !hasAnswer ? (
                    <React.Fragment>
                        <div className="section-title">No AI Suggestion is Available</div>
                        {statusMessage}
                    </React.Fragment>
                ) : (
                    (!details) ? (
                        <AbstractionAiSuggestion
                            answer={answer}
                            keyboardShortcuts={keyboardShortcuts}
                            referenceDocuments={referenceDocuments}
                            onClick={(e, v) => setDetails(v)}
                            feedbackEnabled={feedbackEnabled}
                            onFeedback={(evidenceId, feedback) => onFeedback(evidenceId, feedback)}
                            getFeedback={getFeedback}
                        />
                    ) : (
                        <AbstractionAiDetails
                            details={details}
                            referenceDocuments={referenceDocuments}
                        />
                    )
                )}
            </div>
        </div>
    );
};

AbstractionAiModule.propTypes = {
    isLoading: PropTypes.bool,
    name: PropTypes.string,
    status: PropTypes.string,
    keyComponents: PropTypes.array,
    keyboardShortcuts: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        })
    ),
    error: PropTypes.any,
    referenceDocuments: PropTypes.object,
    answer: PropTypes.object,
    onNavToField: PropTypes.func,
    getFieldLabel: PropTypes.func,
    getEntityLabel: PropTypes.func,
    feedbackEnabled: PropTypes.bool.isRequired,
    onFeedback: PropTypes.func,
    getFeedback: PropTypes.func,
    open: PropTypes.any,
    onToggleOpen: PropTypes.func.isRequired,
};

AbstractionAiModule.defaultProps = {
    name: "",
    status: "",
    error: false,
    isLoading: false,
    keyboardShortcuts: [],
    referenceDocuments: {},
    answer: {}
};

export default AbstractionAiModule;



// WEBPACK FOOTER //
// ./app/lib/dataentry/lib/abstractionai/AbstractionAiModule.js